import { useEffect, useState, useMemo } from "react";
import { Search, SlidersHorizontal, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { OrgCard } from "../components/OrgCard";
import { CategoryIcon } from "../components/CategoryIcon";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet";
import { Skeleton } from "../components/ui/skeleton";

const HERO = "https://images.pexels.com/photos/8555013/pexels-photo-8555013.jpeg";
const CACHE_KEY = "orgs_cache_v1";
const CAT_CACHE = "cats_cache_v1";

export default function Directory() {
  const { t, catLabel } = useLang();
  const [orgs, setOrgs] = useState(() => JSON.parse(localStorage.getItem(CACHE_KEY) || "null") || []);
  const [categories, setCategories] = useState(() => JSON.parse(localStorage.getItem(CAT_CACHE) || "null") || []);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState("all");
  const [loading, setLoading] = useState(orgs.length === 0);

  // Static-style caching: render cached data instantly, then revalidate in the background.
  useEffect(() => {
    Promise.all([api.get("/organizations"), api.get("/categories")])
      .then(([o, c]) => {
        setOrgs(o.data);
        setCategories(c.data);
        localStorage.setItem(CACHE_KEY, JSON.stringify(o.data));
        localStorage.setItem(CAT_CACHE, JSON.stringify(c.data));
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orgs.filter((o) => {
      const matchCat = active === "all" || (o.categories || []).includes(active);
      const matchSearch =
        !q ||
        o.name.toLowerCase().includes(q) ||
        o.short_description.toLowerCase().includes(q) ||
        (o.services || []).some((s) => s.toLowerCase().includes(q)) ||
        (o.categories || []).some((c) => c.toLowerCase().includes(q) || catLabel(c).toLowerCase().includes(q));
      return matchCat && matchSearch;
    });
  }, [orgs, search, active, catLabel]);

  // Group filtered orgs by support type
  const grouped = useMemo(() => {
    const cats = active === "all" ? categories : [active];
    return cats
      .map((cat) => ({ cat, items: filtered.filter((o) => (o.categories || []).includes(cat)) }))
      .filter((g) => g.items.length > 0);
  }, [filtered, categories, active]);

  const FilterList = ({ onPick }) => (
    <div className="flex flex-col gap-1.5" data-testid="filter-list">
      <FilterButton cat="all" label={t("filter_all")} onPick={onPick} active={active} setActive={setActive} />
      {categories.map((c) => (
        <FilterButton key={c} cat={c} label={catLabel(c)} onPick={onPick} active={active} setActive={setActive} />
      ))}
    </div>
  );

  return (
    <div>
      {/* Top search bar */}
      <div className="sticky top-16 z-40 backdrop-blur-xl bg-[#FCFAF8]/90 border-b border-stone-200/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400 rtl:left-auto rtl:right-4" />
            <Input
              data-testid="search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("search_placeholder")}
              className="h-12 ps-12 rounded-full bg-white border-stone-200 text-base focus-visible:ring-2 focus-visible:ring-[#2E604A]"
            />
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12">
        <div className="grid lg:grid-cols-2 gap-8 items-center bg-[#E8EFE9] rounded-3xl p-6 sm:p-10 overflow-hidden">
          <div className="fade-up">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.1em] text-[#2E604A] mb-4">
              <Sparkles className="w-3.5 h-3.5" /> {t("hero_kicker")}
            </span>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-stone-900 leading-[1.05]">
              {t("hero_title")}
            </h1>
            <p className="mt-4 text-base text-stone-700 leading-relaxed max-w-xl">{t("hero_sub")}</p>
            <a href="#directory">
              <Button data-testid="hero-cta" className="mt-6 rounded-full bg-[#2E604A] hover:bg-[#234B3A] text-white h-12 px-7 text-base font-semibold transition-transform hover:-translate-y-0.5">
                {t("hero_cta")}
              </Button>
            </a>
          </div>
          <div className="fade-up" style={{ animationDelay: "120ms" }}>
            <img src={HERO} alt="Community" loading="eager" className="rounded-2xl w-full h-64 lg:h-80 object-cover shadow-lg" />
          </div>
        </div>
      </section>

      {/* Directory */}
      <section id="directory" className="max-w-6xl mx-auto px-4 sm:px-6 mt-10 scroll-mt-32">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Desktop sidebar */}
          <aside className="hidden lg:block lg:col-span-1">
            <div className="sticky top-32">
              <p className="text-xs font-bold uppercase tracking-[0.1em] text-stone-500 mb-3">{t("filter_title")}</p>
              <FilterList />
            </div>
          </aside>

          <div className="lg:col-span-3">
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm text-stone-600" data-testid="results-count">
                {loading ? "…" : filtered.length} {t("results")}
              </p>
              <Sheet>
                <SheetTrigger asChild className="lg:hidden">
                  <Button variant="outline" size="sm" data-testid="mobile-filter-trigger" className="rounded-full gap-2 border-stone-300">
                    <SlidersHorizontal className="w-4 h-4" /> {t("filter_title")}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[85%] max-w-sm">
                  <SheetHeader><SheetTitle className="text-start">{t("filter_title")}</SheetTitle></SheetHeader>
                  <div className="mt-6"><FilterList /></div>
                </SheetContent>
              </Sheet>
            </div>

            {loading && orgs.length === 0 ? (
              <div className="grid sm:grid-cols-2 gap-6">
                {[...Array(4)].map((_, i) => <Skeleton key={`sk-${i}`} className="h-56 rounded-2xl" />)}
              </div>
            ) : grouped.length === 0 ? (
              <div className="text-center py-20 text-stone-500" data-testid="no-results">{t("no_results")}</div>
            ) : (
              <div className="space-y-10">
                {grouped.map((group) => (
                  <div key={group.cat} data-testid={`group-${group.cat}`}>
                    <div className="flex items-center gap-2.5 mb-4">
                      <span className="w-10 h-10 rounded-xl flex items-center justify-center bg-white border-2 border-stone-900 text-stone-900">
                        <CategoryIcon category={group.cat} className="w-5 h-5" />
                      </span>
                      <h2 className="font-display text-xl sm:text-2xl font-bold text-stone-900">{catLabel(group.cat)}</h2>
                      <span className="text-sm text-stone-400 font-semibold">({group.items.length})</span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                      {group.items.map((org, i) => <OrgCard key={`${group.cat}-${org._id}`} org={org} index={i} />)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Submit CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-16">
        <div className="rounded-3xl bg-[#2E604A] p-8 sm:p-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="max-w-xl">
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-white leading-tight">{t("cta_title")}</h2>
            <p className="mt-2 text-white/85 text-base leading-relaxed">{t("cta_desc")}</p>
          </div>
          <a href="/submit" data-testid="directory-submit-cta" className="shrink-0">
            <Button className="rounded-full bg-white text-[#2E604A] hover:bg-[#E8EFE9] h-12 px-7 text-base font-bold transition-transform hover:-translate-y-0.5">
              {t("cta_btn")}
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}

const FilterButton = ({ cat, label, onPick, active, setActive }) => (
  <button
    data-testid={`filter-${cat}`}
    onClick={() => { setActive(cat); onPick && onPick(); }}
    className={`flex items-center gap-2.5 w-full text-start px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
      active === cat ? "bg-[#2E604A] text-white" : "text-stone-700 hover:bg-[#E8EFE9]"
    }`}
  >
    {cat !== "all" && <CategoryIcon category={cat} className="w-4 h-4" />}
    {label}
  </button>
);
